import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Lock, ArrowLeft, Info } from "lucide-react";
import { PageHeader, GlassCard, GradButton, EmptyState, fmtMoney } from "@/components/erp";
import { AkisSatir } from "@/components/AkisSatir";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import api, { errMsg } from "@/lib/api";

const Kilitli = () => {
  const nav = useNavigate();
  return (
    <div className="max-w-xl mx-auto py-16" data-testid="finekra-kilitli">
      <GlassCard className="p-8 text-center space-y-4">
        <div className="w-14 h-14 rounded-2xl grad-bg flex items-center justify-center mx-auto"><Lock className="w-6 h-6 text-white" /></div>
        <div className="font-head text-2xl">Anlık banka hareketleri</div>
        <p className="text-sm text-muted-foreground" data-testid="finekra-kilitli-mesaj">Anlık banka hareketlerini görmek için aboneliğinizi yükseltin.</p>
        <p className="text-xs text-muted-foreground">Abonelik yükseltildiğinde anlaştığınız banka sayısı kadar hesabın hareketleri, tahsile verilen çek / senet tahsilatları dahil burada anlık görünür. Bilgi için program yöneticinizle görüşün.</p>
        <button onClick={() => nav(-1)} data-testid="finekra-geri" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm border border-white/10 hover:bg-white/10"><ArrowLeft className="w-4 h-4" />Geri</button>
      </GlassCard>
    </div>
  );
};

const Gecmis = ({ list }) => (
  <GlassCard className="overflow-x-auto mt-6" data-testid="akis-gecmis">
    <div className="px-4 pt-4 pb-2 text-sm font-medium">İşlenmiş hareketler</div>
    <table className="w-full text-sm min-w-[640px]"><tbody>{list.map((x) => (
      <tr key={x.id} className="border-t border-white/5" data-testid={`akis-islenen-${x.finekra_id}`}>
        <td className="p-3 text-xs">{x.tarih}</td><td className="p-3 text-xs">{x.aciklama}<div className="text-muted-foreground">{x.hesap_ad}</div></td>
        <td className={`p-3 text-right font-mono ${x.tutar < 0 ? "text-red-300" : "text-emerald-300"}`}>{fmtMoney(x.tutar, x.para_birimi)}</td>
        <td className="p-3 text-xs">{x.durum === "yoksayildi" ? "Yoksayıldı" : `${x.islem?.tur} · ${x.islem?.belge || ""}`}{x.islem?.cari_unvan && <div className="text-muted-foreground">{x.islem.cari_unvan}</div>}</td>
      </tr>
    ))}</tbody></table>
  </GlassCard>
);

export default function BankaAkis() {
  const { user } = useAuth();
  const [durum, setDurum] = useState(null);
  const [d, setD] = useState(null);
  const [gecmis, setGecmis] = useState([]);
  const [ek, setEk] = useState({ cariler: [], cekler: [] });
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const s = (await api.get("/finekra/durum")).data;
    setDurum(s);
    if (!s.aktif) return;
    const [a, b, c, t] = await Promise.all([api.get("/finekra/hareketler"), api.get("/finekra/hareketler?durum="), api.get("/cariler"), api.get("/odemeler/ciro-cekler")]);
    setD(a.data); setGecmis(b.data.hareketler.filter((x) => x.durum !== "bekliyor")); setEk({ cariler: c.data.filter((x) => x.aktif !== false), cekler: t.data });
  }, []);
  useEffect(() => { load(); }, [load]);
  const [bankalar, setBankalar] = useState([]);
  useEffect(() => { api.get("/hesaplar").then((r) => setBankalar(r.data.filter((h) => h.tip === "banka" && h.aktif))).catch(() => {}); }, []);
  const senkron = async () => {
    setBusy(true);
    try { const { data } = await api.post("/finekra/senkron"); toast.success(`${data.yeni} yeni hareket alındı`); await load(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (!durum) return null;
  const ust = (
    <>
      <div className="rounded-2xl border border-sky-500/30 bg-sky-500/10 p-3 mb-4 text-sm flex gap-2" data-testid="akis-bilgi"><Info className="w-4 h-4 mt-0.5 shrink-0 text-sky-300" />Online banka hesaplarınızdaki hareketler otomatik olarak alınır. Güncelleme süreleri bankaya göre değişir.</div>
      <GlassCard className="p-4 mb-4" data-testid="akis-bankalarim"><div className="font-semibold mb-2">Bankalarım</div>
        {bankalar.length === 0 ? <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-3">Henüz bağlı banka hesabınız bulunmuyor.<a href="/finans/banka" data-testid="akis-basvuru" className="px-3 py-1.5 rounded-full grad-bg text-white text-xs">Başvuru Yap</a></div>
          : <div className="flex flex-wrap gap-2">{bankalar.map((b) => <span key={b.id} className="text-xs px-3 py-1.5 rounded-full bg-white/5">{b.ad} · {b.banka || b.para_birimi}</span>)}</div>}</GlassCard>
    </>
  );
  if (!durum.aktif) return <>{ust}<Kilitli /></>;
  if (!d) return null;
  return (
    <div data-testid="banka-akis-page">
      {ust}
      <PageHeader eyebrow="Finans · Finekra" title="Banka Hareketleri" desc={`Bağlı ${durum.hesap_sayisi}/${durum.banka_limiti} banka hesabından gelen hareketler. Her satırın altından paranın carilerden nasıl düşüleceğini seçin.${d.son_senkron ? ` Son çekme: ${new Date(d.son_senkron).toLocaleString("tr-TR")}` : ""}`}>
        {can(user, "hesap:write") && <GradButton onClick={senkron} disabled={busy} data-testid="akis-senkron"><RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />Şimdi çek</GradButton>}
      </PageHeader>
      {d.mod === "test" && <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3 mb-4 text-sm text-amber-100" data-testid="akis-test-banner">TEST MODU: hareketler simüle edilir; Finekra API bilgileri gelince canlıya geçilir.</div>}
      <GlassCard data-testid="akis-bekleyen">{d.hareketler.length === 0 ? <EmptyState text="İşlenmeyi bekleyen banka hareketi yok." testid="akis-bos" /> : (
        <div><div className="px-4 pt-4 text-sm font-medium">İşlenmeyi bekleyen ({d.hareketler.length})</div>
          {d.hareketler.map((x) => <AkisSatir key={x.id} x={x} cariler={ek.cariler} cekler={ek.cekler} onDone={load} />)}</div>
      )}</GlassCard>
      {gecmis.length > 0 && <Gecmis list={gecmis} />}
    </div>
  );
}
